/*
 * Carbisdale - GlyphMetrics
 *
 * Zwei-Box-Kerning-Modell für proportionale Fonts.
 * Jedes Zeichen wird durch Body-Box (Baseline bis x-Höhe)
 * und Ascender-Box (x-Höhe bis Cap-Height) beschrieben.
 *
 * Alle Messwerte beziehen sich auf eine feste Referenzgröße
 * und werden beim Rendern linear skaliert.
 */

// === Konstanten ===
// Große Referenzgröße für präzise Kantenerkennung: Bei 200px ist der
// Anti-Aliasing-Saum (~1-2px) vernachlässigbar gegenüber der Zeichenbreite.
// Ergibt Sub-Pixel-Präzision wenn auf Display-Größe herunterskaliert.
export const REFERENCE_SIZE = 200

// Shape-Aware Kerning Faktoren — Defaults (können pro Font überschrieben werden)
export const DEFAULT_KERNING = Object.freeze({
    stepFactor: 0.17,
    capsNose: -0.45,
    capsBelly: -0.20,
    capsBar: 0.20,
    lowerNose: -0.45,
    lowerBelly: -0.20,
    lowerBar: 0.20,
    delta: 0.50,
    blank: 1.5,
    barThreshold: 0.75,
    bellyThreshold: 0.35,
    edgeTolerance: 0.15
})

// Legacy-Exports für Tests (lesen aus DEFAULT_KERNING)
// Entfernt: ALPHA_THRESHOLD — bei REFERENCE_SIZE=200 ist Anti-Aliasing vernachlässigbar

// Zeichen, deren Ascender-Box = Body-Box erzwungen wird
export const FORCE_FULL_ASCENDER = new Set(
    '0123456789()[]{}/<>|!?@#$%^&*+-=~`"\''.split('')
)

// Zeichensatz für Vorab-Messung
export const PRELOAD_CHARS =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz' +
    '0123456789' +
    ' .,;:!?-_\u2013\u2014\'"()[]{}@#$%&*+=/\\|<>^~`\u00AB\u00BB\u201E\u201C\u201D\u201A\u2018\u2019\u2026' +
    '\u00C4\u00D6\u00DC\u00E4\u00F6\u00FC\u00DF' +
    '\u00C0\u00C8\u00C9\u00CA\u00CB\u00CE\u00CF\u00D4\u00D9\u00DB' +
    '\u00E0\u00E8\u00E9\u00EA\u00EB\u00EE\u00EF\u00F4\u00F9\u00FB\u00FF' +
    '\u00C7\u00E7\u00D1\u00F1' +
    '\u20AC\u00A3\u00A5\u00A9\u00AE\u2122'

// === Klassen ===

/**
 * Metriken eines einzelnen Glyphs.
 * Alle Werte in Pixel bei REFERENCE_SIZE.
 */
export class GlyphMetrics {
    constructor(bodyLeft = 0, bodyRight = 0, ascLeft = 0, ascRight = 0, hasAscender = false) {
        this.bodyLeft = bodyLeft
        this.bodyRight = bodyRight
        this.ascLeft = ascLeft
        this.ascRight = ascRight
        this.hasAscender = hasAscender
        // Originale visuelle Ausdehnung (vor Kerning-Adjustments) für Canvas-Dimensionierung
        this.visualRight = bodyRight
    }

    get bodyWidth() { return this.bodyRight - this.bodyLeft }
    get ascWidth() { return this.hasAscender ? this.ascRight - this.ascLeft : 0 }
}

/**
 * Cache für Glyph-Metriken einer Font-Konfiguration.
 * Vier separate Maps für die vier Stil-Kombinationen (kein String-Key-Overhead).
 */
export class GlyphMetricsCache {
    constructor() {
        this.regular = new Map()
        this.bold = new Map()
        this.italic = new Map()
        this.boldItalic = new Map()
        this.xHeightPixels = 0
        this.baseStep = 0
        // On-Demand-Mess-Infrastruktur (wird von buildMetricsCache gesetzt)
        this._ctx = null
        this._refSize = 0
        this._measureConfig = null
        this._variants = null // [{isBold, isItalic, fontString, refs}]
    }

    getMap(isBold, isItalic) {
        if (isBold) {
            return isItalic ? this.boldItalic : this.bold
        }
        return isItalic ? this.italic : this.regular
    }

    get(char, isBold = false, isItalic = false) {
        const map = this.getMap(isBold, isItalic)
        const existing = map.get(char)
        if (existing !== undefined) return existing

        // On-Demand-Messung für unbekannte Zeichen
        if (this._ctx && this._variants) {
            return this._measureOnDemand(char, isBold, isItalic)
        }
        return undefined
    }

    set(char, isBold, isItalic, metrics) {
        this.getMap(isBold, isItalic).set(char, metrics)
    }

    /** Misst ein Zeichen on-demand und cacht das Ergebnis. */
    _measureOnDemand(char, isBold, isItalic) {
        const variant = this._variants.find(v => v.isBold === isBold && v.isItalic === isItalic)
        if (!variant) return undefined

        this._ctx.font = variant.fontString
        const metrics = measureGlyphMetrics(this._ctx, char, variant.refs, this._refSize, this._measureConfig)

        // Emoji-Schutz: fuer Zeichen ab U+2000 (Symbole, Emojis) pruefen ob
        // measureText eine groessere Breite liefert als die Pixel-Analyse.
        // Color-Emojis (z.B. ❄ ⚙) werden vom Browser breiter gerendert
        // als die Text-Font-Version, die measureGlyphMetrics misst.
        const cp = char.codePointAt(0)
        if (cp > 0x2000) {
            const measured = this._ctx.measureText(char).width + 2
            if (measured > metrics.visualRight) {
                metrics.visualRight = measured
                metrics.bodyRight = Math.max(metrics.bodyRight, measured)
            }
        }

        this.set(char, isBold, isItalic, metrics)
        return metrics
    }
}

// === Shape-Klassifikation ===

/**
 * Klassifiziert eine Zeichenkante als 'bar', 'belly', 'nose' oder 'none'.
 * Scannt die volle Zeichenhöhe für eine Seite und misst,
 * wie viele Tintenzeilen nahe am Extremum liegen.
 *
 * Bei REFERENCE_SIZE=200 ist Anti-Aliasing vernachlässigbar (~1px Saum),
 * daher reicht alpha > 0 als Tintenerkennung.
 *
 * @param {Uint8ClampedArray} pixels - Pixel-Daten (RGBA)
 * @param {number} canvasWidth - Breite des Canvas
 * @param {number} topY - Obere Scangrenze (inklusiv)
 * @param {number} bottomY - Untere Scangrenze (inklusiv)
 * @param {'left'|'right'} side - Welche Seite klassifiziert wird
 * @returns {'bar'|'belly'|'nose'|'none'}
 */
export function classifyEdge(pixels, canvasWidth, topY, bottomY, side, kerning = DEFAULT_KERNING) {
    // Für jede Zeile das extremste Pixel finden
    const extremes = []
    let globalExtreme = side === 'right' ? -1 : canvasWidth

    for (let y = topY; y <= bottomY; y++) {
        let rowExtreme = -1
        for (let x = 0; x < canvasWidth; x++) {
            if (pixels[(y * canvasWidth + x) * 4 + 3] > 0) {
                if (side === 'right') {
                    rowExtreme = x
                } else {
                    if (rowExtreme === -1) rowExtreme = x
                }
            }
        }
        if (rowExtreme >= 0) {
            extremes.push(rowExtreme)
            if (side === 'right') {
                if (rowExtreme > globalExtreme) globalExtreme = rowExtreme
            } else {
                if (rowExtreme < globalExtreme) globalExtreme = rowExtreme
            }
        }
    }

    if (extremes.length === 0) return 'none'

    // Spanne der Extremwerte für Toleranzberechnung
    let edgeMin = canvasWidth, edgeMax = -1
    for (const e of extremes) {
        if (e < edgeMin) edgeMin = e
        if (e > edgeMax) edgeMax = e
    }
    const edgeSpan = edgeMax - edgeMin
    const tolerance = Math.max(1, Math.round(edgeSpan * kerning.edgeTolerance))

    // Zählen: wie viele Zeilen liegen innerhalb der Toleranz vom Extremum?
    let closeRows = 0
    for (const e of extremes) {
        if (Math.abs(e - globalExtreme) <= tolerance) closeRows++
    }

    const ratio = closeRows / extremes.length
    if (ratio >= kerning.barThreshold) return 'bar'
    if (ratio >= kerning.bellyThreshold) return 'belly'
    return 'nose'
}

/**
 * Liefert den Kanten-Faktor für eine Shape-Klassifikation.
 *
 * @param {'bar'|'belly'|'nose'|'none'} shape - Kantenform
 * @param {boolean} isCaps - Großbuchstabe?
 * @returns {number} Faktor (-0.45 bis +0.20)
 */
export function resolveEdgeFactor(shape, isCaps, kerning = DEFAULT_KERNING) {
    if (shape === 'none') return 0
    if (isCaps) {
        if (shape === 'nose') return kerning.capsNose
        if (shape === 'belly') return kerning.capsBelly
        return kerning.capsBar
    }
    if (shape === 'nose') return kerning.lowerNose
    if (shape === 'belly') return kerning.lowerBelly
    return kerning.lowerBar
}

// === Pure Functions ===

/**
 * Misst x-Höhe und Cap-Height durch Pixel-Analyse von 'x' und 'H'.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas-Context für Messung
 * @param {number} refSize - Referenz-Schriftgröße (z.B. 40px)
 * @returns {{ xHeight: number, capHeight: number, baseline: number, ascenderHeight: number }}
 */
export function measureFontReferences(ctx, refSize) {
    const baseline = Math.round(refSize * 0.85)
    const canvasHeight = Math.ceil(refSize * 1.5)
    const canvasWidth = Math.ceil(refSize * 2)

    // x-Höhe messen: oberste Tintenspur von 'x'
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    ctx.fillStyle = '#ffffff'
    ctx.textBaseline = 'alphabetic'
    ctx.fillText('x', 0, baseline)
    const xData = ctx.getImageData(0, 0, canvasWidth, canvasHeight).data

    let xHeight = baseline
    outer_x:
    for (let y = 0; y < baseline; y++) {
        for (let x = 0; x < canvasWidth; x++) {
            if (xData[(y * canvasWidth + x) * 4 + 3] > 0) {
                xHeight = y
                break outer_x
            }
        }
    }

    // Cap-Height messen: oberste Tintenspur von 'H'
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    ctx.fillText('H', 0, baseline)
    const hData = ctx.getImageData(0, 0, canvasWidth, canvasHeight).data

    let capHeight = baseline
    outer_h:
    for (let y = 0; y < baseline; y++) {
        for (let x = 0; x < canvasWidth; x++) {
            if (hData[(y * canvasWidth + x) * 4 + 3] > 0) {
                capHeight = y
                break outer_h
            }
        }
    }

    return {
        xHeight,
        capHeight,
        baseline,
        ascenderHeight: xHeight - capHeight
    }
}

/**
 * Misst die Zwei-Box-Metriken für ein einzelnes Zeichen.
 * Shape-Faktoren werden direkt in die Kanten eingerechnet.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas-Context (muss Font bereits gesetzt haben)
 * @param {string} char - Das zu messende Zeichen
 * @param {{ xHeight: number, capHeight: number, baseline: number }} refs - Font-Referenzen
 * @param {number} refSize - Referenz-Schriftgröße
 * @param {{ baseStep?: number }} config - Optionale Konfiguration
 * @returns {GlyphMetrics}
 */
export function measureGlyphMetrics(ctx, char, refs, refSize, config = {}) {
    const baseStep = config.baseStep ?? 0
    const kerning = config.kerning ?? DEFAULT_KERNING

    const canvasHeight = Math.ceil(refSize * 1.5)
    const canvasWidth = Math.ceil(refSize * 2)

    // Zeichen rendern
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    ctx.fillStyle = '#ffffff'
    ctx.textBaseline = 'alphabetic'
    ctx.fillText(char, 0, refs.baseline)

    const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight)
    const pixels = imageData.data

    // Body-Zone scannen (xHeight bis Baseline)
    let bodyMinX = canvasWidth, bodyMaxX = -1
    for (let y = refs.xHeight; y <= refs.baseline; y++) {
        for (let x = 0; x < canvasWidth; x++) {
            if (pixels[(y * canvasWidth + x) * 4 + 3] > 0) {
                if (x < bodyMinX) bodyMinX = x
                if (x > bodyMaxX) bodyMaxX = x
            }
        }
    }

    // Ascender-Zone scannen (capHeight bis xHeight)
    let ascMinX = canvasWidth, ascMaxX = -1
    let hasAscPixels = false
    for (let y = refs.capHeight; y < refs.xHeight; y++) {
        for (let x = 0; x < canvasWidth; x++) {
            if (pixels[(y * canvasWidth + x) * 4 + 3] > 0) {
                if (x < ascMinX) ascMinX = x
                if (x > ascMaxX) ascMaxX = x
                hasAscPixels = true
            }
        }
    }

    // Keine Tinte in der Body-Zone → Descender-Fallback (z.B. Underscore lebt unter der Baseline)
    if (bodyMaxX < 0) {
        // Gesamten Canvas scannen als Fallback
        let fallbackMinX = canvasWidth, fallbackMaxX = -1
        for (let y = 0; y < canvasHeight; y++) {
            for (let x = 0; x < canvasWidth; x++) {
                if (pixels[(y * canvasWidth + x) * 4 + 3] > 0) {
                    if (x < fallbackMinX) fallbackMinX = x
                    if (x > fallbackMaxX) fallbackMaxX = x
                }
            }
        }
        if (fallbackMaxX < 0) {
            return new GlyphMetrics(0, 0, 0, 0, false)
        }
        bodyMinX = fallbackMinX
        bodyMaxX = fallbackMaxX
    }

    // Metriken zusammenstellen
    let bodyLeft = bodyMinX
    let bodyRight = bodyMaxX + 1
    let ascLeft = hasAscPixels ? ascMinX : 0
    let ascRight = hasAscPixels ? ascMaxX + 1 : 0
    let hasAscender = hasAscPixels

    // FORCE_FULL_ASCENDER: Äußere Grenzen beider Zonen
    if (FORCE_FULL_ASCENDER.has(char)) {
        const outerLeft = Math.min(bodyLeft, hasAscPixels ? ascMinX : bodyLeft)
        const outerRight = Math.max(bodyRight, hasAscPixels ? ascMaxX + 1 : bodyRight)
        bodyLeft = outerLeft
        bodyRight = outerRight
        ascLeft = outerLeft
        ascRight = outerRight
        hasAscender = true
    }

    const metrics = new GlyphMetrics(bodyLeft, bodyRight, ascLeft, ascRight, hasAscender)

    // Originale visuelle Ausdehnung VOR Kerning-Adjustments speichern
    // (für Canvas-Dimensionierung im Renderer — Glyphs dürfen nicht beschnitten werden)
    metrics.visualRight = Math.max(bodyRight, hasAscPixels ? ascRight : bodyRight)

    // Delta-Factor: erweitert nur den Body Richtung Ascender,
    // maximal um deltaLimit. Ascender bleibt unverändert, damit
    // computeAdvance Ascender-Kollisionen korrekt erkennt (z.B. "TV").
    // Zweck: verhindert, dass kleine Buchstaben zu weit unter breite
    // Ascender rutschen (z.B. "To"), ohne Großbuchstaben-Paare zu stören.
    if (metrics.hasAscender && baseStep > 0 && !FORCE_FULL_ASCENDER.has(char)) {
        const deltaLimit = kerning.delta * baseStep
        // Linke Seite: Body nach links erweitern wenn Ascender weiter ragt
        if (metrics.ascLeft < metrics.bodyLeft) {
            metrics.bodyLeft = Math.max(metrics.bodyLeft - deltaLimit, metrics.ascLeft)
        }
        // Rechte Seite: Body nach rechts erweitern wenn Ascender weiter ragt
        if (metrics.ascRight > metrics.bodyRight) {
            metrics.bodyRight = Math.min(metrics.bodyRight + deltaLimit, metrics.ascRight)
        }
    }

    // Shape-Klassifikation: jede Box unabhängig analysieren und adjustieren
    if (baseStep > 0) {
        const isCaps = char !== char.toLowerCase() && char === char.toUpperCase()

        // Body-Shape (xHeight bis Baseline)
        const bodyLeftShape = classifyEdge(pixels, canvasWidth, refs.xHeight, refs.baseline, 'left', kerning)
        const bodyRightShape = classifyEdge(pixels, canvasWidth, refs.xHeight, refs.baseline, 'right', kerning)
        metrics.bodyLeft -= resolveEdgeFactor(bodyLeftShape, isCaps, kerning) * baseStep
        metrics.bodyRight += resolveEdgeFactor(bodyRightShape, isCaps, kerning) * baseStep

        // Ascender-Shape (capHeight bis xHeight)
        if (metrics.hasAscender && !FORCE_FULL_ASCENDER.has(char)) {
            const ascLeftShape = classifyEdge(pixels, canvasWidth, refs.capHeight, refs.xHeight, 'left', kerning)
            const ascRightShape = classifyEdge(pixels, canvasWidth, refs.capHeight, refs.xHeight, 'right', kerning)
            metrics.ascLeft -= resolveEdgeFactor(ascLeftShape, isCaps, kerning) * baseStep
            metrics.ascRight += resolveEdgeFactor(ascRightShape, isCaps, kerning) * baseStep
        }
    }

    return metrics
}

/**
 * Stellt sicher, dass der Cache Blank-Metriken enthält.
 * Space bekommt eine feste Breite basierend auf baseStep.
 */
function _ensureBlankMetrics(cache, kerning = DEFAULT_KERNING) {
    const blankWidth = kerning.blank * cache.baseStep
    const blankMetrics = new GlyphMetrics(0, blankWidth, 0, blankWidth, true)
    // Für alle vier Varianten setzen
    cache.set(' ', false, false, blankMetrics)
    cache.set(' ', true, false, blankMetrics)
    cache.set(' ', false, true, blankMetrics)
    cache.set(' ', true, true, blankMetrics)
}

/**
 * Baut den Metriken-Cache für eine Font-Konfiguration.
 * Misst alle vier Varianten (regular/bold/italic/boldItalic).
 *
 * @param {{ family: string, familyBold: string, familyItalic: string, familyBoldItalic: string }} fontConfig
 * @param {number} refSize - Referenzgröße für Pixel-Analyse
 * @param {Object} config - Optionale Konfiguration
 * @returns {GlyphMetricsCache}
 */
export function buildMetricsCache(fontConfig, refSize, config = {}) {
    const cache = new GlyphMetricsCache()

    const canvasWidth = Math.ceil(refSize * 2)
    const canvasHeight = Math.ceil(refSize * 1.5)
    const canvas = new OffscreenCanvas(canvasWidth, canvasHeight)
    const ctx = canvas.getContext('2d', { willReadFrequently: true })

    // Regular-Refs zuerst messen um xHeightPixels und baseStep zu bestimmen
    const regularFontString = `${refSize}px ${fontConfig.family}`
    ctx.font = regularFontString
    const regularRefs = measureFontReferences(ctx, refSize)
    cache.xHeightPixels = regularRefs.baseline - regularRefs.xHeight
    // Kerning-Faktoren: Font-spezifische Overrides oder Defaults
    const kerning = fontConfig.kerning
        ? { ...DEFAULT_KERNING, ...fontConfig.kerning }
        : DEFAULT_KERNING
    cache.kerning = kerning

    cache.baseStep = kerning.stepFactor * cache.xHeightPixels

    // Vier Varianten messen
    const variants = [
        { isBold: false, isItalic: false, family: fontConfig.family, weight: '' },
        { isBold: true, isItalic: false, family: fontConfig.familyBold, weight: 'bold ' },
        { isBold: false, isItalic: true, family: fontConfig.familyItalic || fontConfig.family, weight: '' },
        { isBold: true, isItalic: true, family: fontConfig.familyBoldItalic || fontConfig.familyBold, weight: 'bold ' }
    ]

    const measureConfig = { ...config, baseStep: cache.baseStep, kerning }

    // Varianten-Info für On-Demand-Messung sammeln
    const variantInfos = []

    // Emoji-Fallback-Fonts: identisch mit Renderer._getFontStringForConfig
    const emojiFallback = ', "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"'

    for (const variant of variants) {
        const fontString = `${variant.weight}${refSize}px ${variant.family}${emojiFallback}`
        ctx.font = fontString

        // Referenzlinien einmal pro Variante messen
        const refs = measureFontReferences(ctx, refSize)

        // Für On-Demand-Messung speichern
        variantInfos.push({ isBold: variant.isBold, isItalic: variant.isItalic, fontString, refs })

        // Alle Vorab-Zeichen messen
        for (const char of PRELOAD_CHARS) {
            ctx.font = fontString
            const metrics = measureGlyphMetrics(ctx, char, refs, refSize, measureConfig)
            cache.set(char, variant.isBold, variant.isItalic, metrics)
        }
    }

    // On-Demand-Mess-Infrastruktur im Cache speichern
    cache._ctx = ctx
    cache._refSize = refSize
    cache._measureConfig = measureConfig
    cache._variants = variantInfos

    // Space mit fester Breite überschreiben
    _ensureBlankMetrics(cache, kerning)

    return cache
}

/**
 * Berechnet den Advance zwischen zwei aufeinanderfolgenden Zeichen.
 * Zwei-Box-Kerning: Body-Zone + Ascender-Zone.
 * Shape-Faktoren stecken bereits in den Box-Kanten.
 *
 * @param {GlyphMetrics} left - Metriken des linken Zeichens
 * @param {GlyphMetrics} right - Metriken des rechten Zeichens
 * @param {number} baseStep - Basis-Abstand in Pixel
 * @returns {number} Advance in Pixel (bei Referenzgröße)
 */
export function computeAdvance(left, right, baseStep) {
    const bodyAdvance = left.bodyRight - right.bodyLeft + baseStep

    // Ascender-Constraint nur wenn BEIDE Zeichen Ascender haben
    if (left.hasAscender && right.hasAscender) {
        const ascAdvance = left.ascRight - right.ascLeft + baseStep
        if (ascAdvance > bodyAdvance) return ascAdvance
    }

    return bodyAdvance
}

/**
 * Berechnet das Layout (x-Position und Breite) für jeden Buchstaben eines Strings.
 *
 * @param {string} text - Der zu layoutende Text
 * @param {GlyphMetricsCache} cache - Metriken-Cache
 * @param {number} baseStep - Basis-Abstand in Pixel
 * @param {Array<{ start: number, end: number, bold: boolean, italic: boolean }>} formatRuns - Formatierung
 * @returns {{ positions: Float32Array, widths: Float32Array, totalWidth: number }}
 */
export function computeStringLayout(text, cache, baseStep, formatRuns = null) {
    // Codepoint-aware: Array.from() splittet Surrogate-Paare korrekt
    const codepoints = Array.from(text)
    const len = codepoints.length
    if (len === 0) {
        return { positions: new Float32Array(0), widths: new Float32Array(0), totalWidth: 0 }
    }

    const positions = new Float32Array(len)
    const widths = new Float32Array(len)

    // Format-Lookup: Cursor-basiert O(n+r) statt O(n×r)
    const runCount = (formatRuns && formatRuns.length) ? formatRuns.length : 0
    let runIdx = 0

    let x = 0
    let prevMetrics = null
    let lastMetrics = null

    for (let i = 0; i < len; i++) {
        // Run-Cursor vorrücken (formatRuns sind sortiert und nicht-überlappend)
        while (runIdx < runCount && i >= formatRuns[runIdx].end) runIdx++
        const inRun = runIdx < runCount && i >= formatRuns[runIdx].start
        const isBold = inRun ? formatRuns[runIdx].bold : false
        const isItalic = inRun ? formatRuns[runIdx].italic : false
        const metrics = cache.get(codepoints[i], isBold, isItalic)

        if (!metrics) {
            // Fallback: feste Breite (nur wenn On-Demand-Messung nicht verfügbar, z.B. in Tests)
            positions[i] = x
            widths[i] = baseStep || 10
            x += baseStep || 10
            prevMetrics = null
            continue
        }

        if (i === 0) {
            // Erstes Zeichen: bei x=0 starten
            positions[i] = 0
            widths[i] = metrics.bodyWidth
            x = metrics.bodyRight
            prevMetrics = metrics
        } else if (prevMetrics) {
            // Advance vom Vorgänger berechnen
            const advance = computeAdvance(prevMetrics, metrics, baseStep)
            const prevStart = positions[i - 1]
            positions[i] = prevStart + advance
            widths[i] = metrics.bodyWidth
            x = positions[i] + metrics.bodyRight
            prevMetrics = metrics
        } else {
            // Vorgänger hatte keine Metriken — an aktuelle x-Position setzen
            positions[i] = x
            widths[i] = metrics.bodyWidth
            x = positions[i] + metrics.bodyRight
            prevMetrics = metrics
        }
        lastMetrics = metrics
    }

    // totalWidth: Position des letzten Zeichens + maximale rechte Ausdehnung
    // Am Zeilenende muss der Ascender berücksichtigt werden (z.B. "T"-Querbalken),
    // damit Cursor und Absatzmarke nicht unter dem Zeichen stehen
    let endRight = lastMetrics ? lastMetrics.bodyRight : widths[len - 1]
    if (lastMetrics && lastMetrics.hasAscender && lastMetrics.ascRight > endRight) {
        endRight = lastMetrics.ascRight
    }
    const totalWidth = positions[len - 1] + endRight

    return { positions, widths, totalWidth }
}

